// Where the numbers for a voice campaign come from.
//
// Three sources, one output: a de-duplicated RecipientInput[]. The de-duplication
// is the point. The runtime accepts whatever it is given and dials all of it, so
// a number queued twice is a customer called twice — and the two ways that
// happens in practice are pasting a list that already overlaps and queueing the
// same selection onto a campaign a second time. The first is caught here; the
// second is caught by showing what is already queued (see AIVoiceCampaigns).
//
// Params matter as much as the number: they arrive at the runtime as customParam
// and are what the agent uses to say a caller's name instead of "hello there".
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Upload, ClipboardList, Loader2, AlertCircle, Check } from 'lucide-react';
import { RecipientInput } from 'api/services/voiceCampaigns.service';
import { callerIdHeadings } from './recipientRules';
import { contactsService } from 'api/services/contacts.service';
import { Contact } from 'types';
import {
  collapse, parseCsv, recipientsFromCsv, EMPTY_SELECTION, RecipientSelection,
} from './recipientRules';

export type { RecipientSelection };
export { dedupeKey } from './recipientRules';

/** How many contacts one "select all" may pull. Guards the browser, not the API. */
const SELECT_ALL_CAP = 5000;
const PAGE = 50;

interface Props {
  onChange: (sel: RecipientSelection) => void;
  inputClass: string;
  labelClass: string;
  /**
   * Let the same number appear more than once in one submission. Repeats are
   * still counted and reported either way — the point is that doubling a list
   * must be a decision, never an accident.
   */
  allowRepeats?: boolean;
}

const RecipientSources: React.FC<Props> = ({ onChange, inputClass, labelClass, allowRepeats = false }) => {
  const [source, setSource] = useState<'paste' | 'contacts' | 'csv'>('paste');

  // ── paste ──
  const [pasted, setPasted] = useState('');

  // ── contacts ──
  const [query, setQuery] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);
  // Selected across pages, so paging away does not silently drop a choice.
  const [picked, setPicked] = useState<Map<string, RecipientInput>>(new Map());
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // ── csv ──
  const [csvRows, setCsvRows] = useState<RecipientInput[]>([]);
  const [csvNote, setCsvNote] = useState<string | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  /** Read fine, but a column will not do what its name suggests. */
  const [csvWarn, setCsvWarn] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /** The primary number, falling back to the first one on file. */
  const phoneOf = (c: Contact): string => {
    const list = c.phone_numbers || [];
    return (list.find(p => p.is_primary) || list[0])?.number || '';
  };

  /** What the agent can say about this person. Blank fields are not sent. */
  const paramsOf = (c: Contact): Record<string, any> | undefined => {
    const p: Record<string, any> = {};
    const name = c.name || [c.first_name, c.last_name].filter(Boolean).join(' ');
    if (name) p.name = name;
    if (c.first_name) p.first_name = c.first_name;
    if (c.company) p.company = c.company;
    return Object.keys(p).length ? p : undefined;
  };

  const fetchContacts = useCallback(async (p: number, q: string) => {
    setLoadingContacts(true); setContactsError(null);
    const res = await contactsService.getContacts({ page: p, size: PAGE, search: q || undefined });
    if (res.success && res.data) {
      setContacts(res.data.data || []);
      setTotal(res.data.total || 0);
    } else {
      setContacts([]); setTotal(0);
      setContactsError(res.error || 'Could not load contacts');
    }
    setLoadingContacts(false);
  }, []);

  useEffect(() => {
    if (source !== 'contacts') return;
    const t = setTimeout(() => fetchContacts(page, query), 250);
    return () => clearTimeout(t);
  }, [source, page, query, fetchContacts]);

  const toggle = (c: Contact) => {
    const phone = phoneOf(c);
    if (!phone) return;
    const next = new Map(picked);
    const uid = c._uid;
    if (next.has(uid)) next.delete(uid);
    else next.set(uid, { phone, params: paramsOf(c) });
    setPicked(next);
    setBulkNote(null);
  };

  /**
   * Adds every contact matching the current search, not just the visible page.
   *
   * Paging through 4,766 contacts by hand is not a workflow, but pulling them
   * all is also the moment a mis-typed search becomes a campaign to everyone —
   * so the count is stated plainly before and after.
   */
  const selectAllMatching = async () => {
    setBulkBusy(true); setBulkNote(null);
    const next = new Map(picked);
    let fetched = 0, withoutNumber = 0, p = 1;
    while (fetched < Math.min(total, SELECT_ALL_CAP)) {
      const res = await contactsService.getContacts({ page: p, size: PAGE, search: query || undefined });
      if (!res.success || !res.data) { setContactsError(res.error || 'Could not load all matches'); break; }
      const batch = res.data.data || [];
      if (!batch.length) break;
      for (const c of batch) {
        const phone = phoneOf(c);
        if (!phone) { withoutNumber++; continue; }
        next.set(c._uid, { phone, params: paramsOf(c) });
      }
      fetched += batch.length;
      p++;
    }
    setPicked(next);
    setBulkBusy(false);
    setBulkNote(
      `${next.size} selected` +
      (total > SELECT_ALL_CAP ? ` — stopped at the ${SELECT_ALL_CAP.toLocaleString()} cap of ${total.toLocaleString()} matches` : '') +
      (withoutNumber ? ` · ${withoutNumber} skipped with no number on file` : ''),
    );
  };

  const onFile = async (f: File) => {
    setCsvError(null); setCsvNote(null); setCsvWarn(null); setCsvRows([]);
    const rows = parseCsv(await f.text());
    if (!rows.length) { setCsvError('That file has no rows.'); return; }

    const { recipients, hasHeader, column } = recipientsFromCsv(rows);
    const others = hasHeader ? rows[0].length - 1 : 0;
    setCsvRows(recipients);
    setCsvNote(
      hasHeader
        ? `Read ${recipients.length.toLocaleString()} rows — number from "${column}"` +
          (others > 0 ? `, ${others} other column(s) passed to the agent` : '')
        : `Read ${recipients.length.toLocaleString()} rows — no header recognised, using the first column as the number`,
    );

    // A caller_id column is the one that looks like it works and does not. It
    // becomes a variable the agent is told, never the number presented — the
    // caller ID is one value per submission, taken from the campaign. Said here
    // because the file is otherwise accepted without a murmur.
    const callerCols = hasHeader ? callerIdHeadings(rows[0]) : [];
    if (callerCols.length) {
      setCsvWarn(
        `The "${callerCols[0]}" column will NOT set the number these calls present. ` +
        `A caller ID is one value for the whole submission and comes from the campaign — ` +
        `this column is passed to the agent as information instead. Set the caller ID on the ` +
        `campaign if you need to change it.`,
      );
    }
  };

  // The single output. Recomputed from whichever source is showing, so switching
  // tabs cannot leave a stale selection queued from a source no longer visible.
  const selection = useMemo<RecipientSelection>(() => {
    if (source === 'paste') {
      const rows = pasted.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean).map(phone => ({ phone }));
      return rows.length ? collapse(rows, allowRepeats) : EMPTY_SELECTION;
    }
    if (source === 'contacts') {
      const rows = Array.from(picked.values());
      return rows.length ? collapse(rows, allowRepeats) : EMPTY_SELECTION;
    }
    return csvRows.length ? collapse(csvRows, allowRepeats) : EMPTY_SELECTION;
  }, [source, pasted, picked, csvRows, allowRepeats]);

  useEffect(() => { onChange(selection); }, [selection, onChange]);

  const tab = (id: typeof source, label: string, Icon: any) => (
    <button
      type="button"
      onClick={() => setSource(id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-lg border transition ${
        source === id
          ? 'border-transparent bg-gray-900 text-white dark:bg-white dark:text-gray-900'
          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div>
      <div className="flex gap-2 flex-wrap">
        {tab('paste', 'Paste numbers', ClipboardList)}
        {tab('contacts', 'From contacts', Search)}
        {tab('csv', 'Upload CSV', Upload)}
      </div>

      {source === 'paste' && (
        <div className="mt-3">
          <label className={labelClass}>One per line, or comma separated</label>
          <textarea
            className={inputClass + ' font-mono h-40'}
            value={pasted}
            onChange={e => setPasted(e.target.value)}
            placeholder={'+919876543210\n+919876543211'}
          />
        </div>
      )}

      {source === 'contacts' && (
        <div className="mt-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              className={inputClass + ' pl-9'}
              value={query}
              onChange={e => { setQuery(e.target.value); setPage(1); }}
              placeholder="Search by name, company or number"
            />
          </div>

          <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
            <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
              {loadingContacts ? 'Searching…' : `${total.toLocaleString()} match${total === 1 ? '' : 'es'}`}
            </span>
            <div className="flex items-center gap-2">
              {picked.size > 0 && (
                <button type="button" className="text-[12px] underline" onClick={() => { setPicked(new Map()); setBulkNote(null); }}>
                  Clear {picked.size}
                </button>
              )}
              {total > 0 && (
                <button
                  type="button"
                  className="text-[12px] underline disabled:opacity-50"
                  disabled={bulkBusy}
                  onClick={selectAllMatching}
                >
                  {bulkBusy ? 'Selecting…' : `Select all ${Math.min(total, SELECT_ALL_CAP).toLocaleString()}`}
                </button>
              )}
            </div>
          </div>

          {bulkNote && (
            <div className="mt-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>{bulkNote}</div>
          )}
          {contactsError && (
            <div className="mt-2 flex items-center gap-1.5 text-[12px] text-red-600">
              <AlertCircle className="w-3.5 h-3.5" />{contactsError}
            </div>
          )}

          <div className="mt-2 border rounded-xl divide-y max-h-56 overflow-y-auto"
               style={{ borderColor: 'var(--border)' }}>
            {loadingContacts && contacts.length === 0 ? (
              <div className="flex items-center gap-2 text-[13px] p-3" style={{ color: 'var(--text-muted)' }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-[13px] p-3" style={{ color: 'var(--text-muted)' }}>
                No contacts match that search.
              </div>
            ) : contacts.map(c => {
              const uid = c._uid;
              const phone = phoneOf(c);
              const on = picked.has(uid);
              const name = c.name || [c.first_name, c.last_name].filter(Boolean).join(' ');
              return (
                <button
                  type="button"
                  key={uid}
                  onClick={() => toggle(c)}
                  disabled={!phone}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left disabled:opacity-50"
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    on ? 'bg-gray-900 border-gray-900 dark:bg-white dark:border-white' : 'border-gray-400'
                  }`}>
                    {on && <Check className="w-3 h-3 text-white dark:text-gray-900" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] text-gray-900 dark:text-white truncate">
                      {name || '(no name)'}
                    </span>
                    <span className="block text-[11.5px] font-mono" style={{ color: 'var(--text-muted)' }}>
                      {phone || 'no number on file'}
                      {c.company ? ` · ${c.company}` : ''}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-2 text-[12px]">
              <button type="button" className="underline disabled:opacity-40" disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}>Previous</button>
              <span style={{ color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
              <button type="button" className="underline disabled:opacity-40" disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </div>
      )}

      {source === 'csv' && (
        <div className="mt-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full border border-dashed rounded-xl py-6 text-[13px] flex flex-col items-center gap-1.5"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
          >
            <Upload className="w-5 h-5" />
            Choose a CSV file
          </button>
          <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
            One column must hold the number — a header named phone, mobile, number or msisdn is
            found automatically. Every other column is passed to the agent, so a
            <span className="font-mono"> name </span> column lets it greet people by name.
          </p>
          {csvNote && <div className="mt-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>{csvNote}</div>}
          {csvWarn && (
            <div className="mt-2 text-[12px] rounded px-2.5 py-2 flex items-start gap-1.5
                            bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{csvWarn}</span>
            </div>
          )}
          {csvError && (
            <div className="mt-2 flex items-center gap-1.5 text-[12px] text-red-600">
              <AlertCircle className="w-3.5 h-3.5" />{csvError}
            </div>
          )}
        </div>
      )}

      {/* What will actually be queued — stated before the button is pressed,
          because after it is pressed the calls are already scheduled. */}
      <div className="mt-3 text-[12.5px] px-3 py-2 rounded-lg"
           style={{ background: 'var(--surface-2, rgba(127,127,127,0.08))' }}>
        <b>{selection.recipients.length.toLocaleString()}</b> will be queued
        {selection.duplicates > 0 && <> · {selection.duplicates.toLocaleString()} duplicate{selection.duplicates === 1 ? '' : 's'} removed</>}
        {selection.invalid > 0 && <> · {selection.invalid.toLocaleString()} not a phone number</>}
      </div>
    </div>
  );
};

export default RecipientSources;
